import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Progress,
  Table,
  Space,
  Button,
  Typography,
  Empty,
  Tag,
  Drawer,
  Descriptions,
  Divider,
  Spin,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Tooltip,
  Alert,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ProjectService } from '../../services/projectService';
import { StatisticsService } from '../../services/statisticsService';
import api from '../../services/api';
import { CoordinationCostService } from '../../services/coordinationCostService';
import { CoordinationCostType, ProjectStatus } from '../../types';

const { Title, Text } = Typography;
const { TextArea } = Input;
type QuickAddType = 'subcontract' | 'coordination';

const formatMoney = (num: number) => `¥${(num || 0).toLocaleString()}`;

const getUsageLevel = (usage: number) => {
  if (usage > 100) return { key: 'over', label: '超支', color: 'red' };
  if (usage >= 85) return { key: 'danger', label: '危险', color: 'orange' };
  if (usage >= 70) return { key: 'warning', label: '预警', color: 'gold' };
  return { key: 'healthy', label: '健康', color: 'green' };
};

const DASHBOARD_THEME = {
  pageBackground: 'linear-gradient(180deg, #d8e0e5 0%, #c2ccd3 42%, #aab6c0 100%)',
  cardBackground: 'rgba(224, 230, 234, 0.82)',
  cardBackgroundStrong: 'rgba(214, 221, 226, 0.92)',
  softBorder: '1px solid rgba(16, 27, 35, 0.08)',
  shadow: '0 26px 56px rgba(46, 60, 70, 0.14)',
  accent: '#b99258',
  accentSecondary: '#547590',
  textPrimary: '#10171d',
  textSecondary: '#455763',
  textMuted: '#667986'
};

interface ProjectControlCenterProps {
  embedded?: boolean;
}

const ProjectControlCenter: React.FC<ProjectControlCenterProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const [quickAddVisible, setQuickAddVisible] = useState(false);
  const [quickAddType, setQuickAddType] = useState<QuickAddType>('subcontract');
  const [quickAddForm] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['project-control-center'],
    queryFn: async () => {
      const response = await ProjectService.getProjects({ page: 1, limit: 200 });
      return response;
    },
  });

  const projects = data?.data || [];

  useEffect(() => {
    const quickAdd = searchParams.get('quickAdd');
    const projectId = searchParams.get('projectId');
    if ((quickAdd === 'subcontract' || quickAdd === 'coordination') && projects.length > 0) {
      setQuickAddType(quickAdd);
      setQuickAddVisible(true);
      quickAddForm.setFieldsValue({
        projectId: projectId || undefined,
        costDate: dayjs(),
        status: 'active',
        costType: CoordinationCostType.MISC,
        paidAmount: 0,
      });
    }
  }, [searchParams, projects, quickAddForm]);

  const clearQuickAddQuery = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('quickAdd');
    setSearchParams(next);
  };

  const openQuickAdd = (type: QuickAddType) => {
    setQuickAddType(type);
    setQuickAddVisible(true);
    quickAddForm.resetFields();
    quickAddForm.setFieldsValue({
      costDate: dayjs(),
      status: 'active',
      costType: CoordinationCostType.MISC,
      paidAmount: 0,
    });
  };

  const closeQuickAdd = () => {
    setQuickAddVisible(false);
    quickAddForm.resetFields();
    clearQuickAddQuery();
  };

  const createSubcontractMutation = useMutation({
    mutationFn: (payload: any) => api.post('/subcontract-costs', payload),
    onSuccess: () => {
      message.success('分包成本记录创建成功');
      queryClient.invalidateQueries({ queryKey: ['project-control-center'] });
      closeQuickAdd();
    },
    onError: () => message.error('分包成本记录创建失败'),
  });

  const createCoordinationMutation = useMutation({
    mutationFn: (payload: any) => CoordinationCostService.createCoordinationCost(payload),
    onSuccess: () => {
      message.success('协调成本记录创建成功');
      queryClient.invalidateQueries({ queryKey: ['project-control-center'] });
      closeQuickAdd();
    },
    onError: () => message.error('协调成本记录创建失败'),
  });

  const handleQuickAddSubmit = async () => {
    const values = await quickAddForm.validateFields();

    if (quickAddType === 'subcontract') {
      createSubcontractMutation.mutate({
        projectId: values.projectId,
        contractName: values.contractName,
        costDate: values.costDate.format('YYYY-MM-DD'),
        contractAmount: values.contractAmount,
        paidAmount: values.paidAmount || 0,
        status: values.status,
        description: values.description,
      });
      return;
    }

    createCoordinationMutation.mutate({
      projectId: values.projectId,
      costDate: values.costDate.format('YYYY-MM-DD'),
      costType: values.costType,
      amount: values.amount,
      handler: values.handler,
      description: values.description,
    });
  };

  const projectWithUsage = useMemo(() => {
    return projects.map((p) => {
      const budget = p.budget || 0;
      const actual = p.actualCost || 0;
      const usage = typeof p.budgetUsageRate === 'number' ? p.budgetUsageRate : (budget > 0 ? (actual / budget) * 100 : 0);
      return { ...p, usage, level: getUsageLevel(usage) };
    });
  }, [projects]);

  const summary = useMemo(() => {
    const totalBudget = projectWithUsage.reduce((sum, p) => sum + (p.budget || 0), 0);
    const totalActual = projectWithUsage.reduce((sum, p) => sum + (p.actualCost || 0), 0);
    const activeCount = projectWithUsage.filter((p) => p.status === ProjectStatus.ACTIVE || p.status === '进行中').length;
    const levelCounts = {
      healthy: projectWithUsage.filter((p) => p.level.key === 'healthy').length,
      warning: projectWithUsage.filter((p) => p.level.key === 'warning').length,
      danger: projectWithUsage.filter((p) => p.level.key === 'danger').length,
      over: projectWithUsage.filter((p) => p.level.key === 'over').length,
    };

    return {
      totalBudget,
      totalActual,
      activeCount,
      overBudgetCount: projectWithUsage.filter((p) => p.riskTags?.includes('超预算')).length,
      noBudgetCount: projectWithUsage.filter((p) => p.riskTags?.includes('无预算')).length,
      overdueCount: projectWithUsage.filter((p) => p.riskTags?.includes('超期')).length,
      staleCount: projectWithUsage.filter((p) => p.riskTags?.includes('长期无更新')).length,
      usageRate: totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0,
      levelCounts,
    };
  }, [projectWithUsage]);

  const [riskFilter, setRiskFilter] = useState<string>('all');

  const riskProjects = useMemo(() => {
    const filtered = riskFilter === 'all'
      ? projectWithUsage
      : projectWithUsage.filter((project) => project.riskTags?.includes(riskFilter));

    return [...filtered]
      .sort((a, b) => (b.riskScore || 0) - (a.riskScore || 0) || (b.usage || 0) - (a.usage || 0))
      .slice(0, 20);
  }, [projectWithUsage, riskFilter]);

  const drilldownQuery = useQuery({
    queryKey: ['project-cost-drilldown', selectedProject?._id],
    enabled: !!selectedProject?._id,
    queryFn: async () => {
      if (!selectedProject?._id) return null;
      const resp = await StatisticsService.getProjectComprehensiveStats(selectedProject._id);
      return resp.success ? resp.data : null;
    },
  });

  const columns = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: any) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary">{record.projectCode || '无编码'}</Text>
        </Space>
      ),
    },
    {
      title: '预算',
      dataIndex: 'budget',
      key: 'budget',
      render: (v: number) => formatMoney(v || 0),
    },
    {
      title: '累计统计成本',
      dataIndex: 'actualCost',
      key: 'actualCost',
      render: (v: number) => formatMoney(v || 0),
    },
    {
      title: '预警等级',
      key: 'level',
      render: (_: any, record: any) => <Tag color={record.level.color}>{record.level.label}</Tag>,
    },
    {
      title: '风险标签',
      key: 'riskTags',
      render: (_: any, record: any) => (
        <Space wrap>
          {(record.riskTags || []).length > 0
            ? record.riskTags.map((tag: string) => <Tag key={tag} color={tag === '超预算' ? 'red' : tag === '超期' ? 'orange' : 'gold'}>{tag}</Tag>)
            : <Tag>关注中</Tag>}
        </Space>
      ),
    },
    {
      title: '预算健康',
      key: 'usage',
      render: (_: any, record: any) => {
        const usage = record.usage || 0;
        const status = usage > 100 ? 'exception' : usage >= 85 ? 'active' : 'success';
        return (
          <Space orientation="vertical" size={4} style={{ width: 160 }}>
            <Progress percent={Number(usage.toFixed(1))} status={status as any} size="small" />
            <Text type={usage > 100 ? 'danger' : undefined}>{usage.toFixed(1)}%</Text>
          </Space>
        );
      },
    },
    {
      title: '风险原因',
      key: 'riskReasons',
      render: (_: any, record: any) => (
        <Text type="secondary">{record.riskReasons?.[0] || '暂无明显异常'}</Text>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedProject(record);
            setDrillOpen(true);
          }}
        >
          总成本下钻
        </Button>
      ),
    },
  ];

  const breakdown = drilldownQuery.data?.summary?.costBreakdown;
  const totalProjectCost = drilldownQuery.data?.summary?.totalProjectCost || 0;
  const focusProject = riskProjects[0];
  const summaryCards = [
    {
      key: 'budget',
      title: '总预算盘面',
      value: formatMoney(summary.totalBudget),
      detail: `${projectWithUsage.length} 个项目`,
      accent: '#f6c453'
    },
    {
      key: 'actual',
      title: '累计统计成本',
      value: formatMoney(summary.totalActual),
      detail: `${summary.activeCount} 个项目进行中`,
      accent: '#65d6b8'
    },
    {
      key: 'usage',
      title: '总预算使用率',
      value: `${summary.usageRate.toFixed(1)}%`,
      detail: summary.overBudgetCount > 0 ? `${summary.overBudgetCount} 个项目超预算` : '整体预算可控',
      accent: '#7a8cff'
    },
    {
      key: 'overdue',
      title: '异常更新',
      value: `${summary.staleCount + summary.overdueCount}`,
      detail: `${summary.overdueCount} 个超期 / ${summary.staleCount} 个未更新`,
      accent: '#f46b6b'
    }
  ];

  return (
    <div
      style={{
        background: embedded ? 'transparent' : DASHBOARD_THEME.pageBackground,
        borderRadius: embedded ? 0 : 32,
        padding: embedded ? 0 : 24,
        boxShadow: embedded ? 'none' : DASHBOARD_THEME.shadow,
        border: embedded ? 'none' : DASHBOARD_THEME.softBorder,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {!embedded && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at top left, rgba(84,117,144,0.14) 0%, transparent 28%), radial-gradient(circle at 85% 15%, rgba(185,146,88,0.14) 0%, transparent 20%)',
            pointerEvents: 'none'
          }}
        />
      )}

      <Space orientation="vertical" size="large" style={{ width: '100%', position: 'relative' }}>
        {!embedded && (
          <Space orientation="vertical" size={4} style={{ marginBottom: 4 }}>
            <Title level={2} style={{ marginBottom: 0, color: DASHBOARD_THEME.textPrimary }}>项目风险工作台</Title>
          </Space>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16
          }}
        >
          {summaryCards.map((item) => (
            <Card
              key={item.key}
              variant="borderless"
              style={{
                borderRadius: 24,
                background: DASHBOARD_THEME.cardBackground,
                border: DASHBOARD_THEME.softBorder,
                boxShadow: DASHBOARD_THEME.shadow
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Space orientation="vertical" size={10} style={{ width: '100%' }}>
                <Text style={{ color: DASHBOARD_THEME.textSecondary }}>{item.title}</Text>
                <div style={{ color: DASHBOARD_THEME.textPrimary, fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
                  {item.value}
                </div>
                <div style={{ color: DASHBOARD_THEME.textMuted, fontSize: 13 }}>{item.detail}</div>
                <div style={{ width: 42, height: 4, borderRadius: 999, background: item.accent }} />
              </Space>
            </Card>
          ))}
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card
              variant="borderless"
              title={<span style={{ color: DASHBOARD_THEME.textPrimary }}>风险总览</span>}
              loading={isLoading}
              style={{
                borderRadius: 28,
                background: DASHBOARD_THEME.cardBackground,
                border: DASHBOARD_THEME.softBorder,
                boxShadow: DASHBOARD_THEME.shadow
              }}
              headStyle={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
              bodyStyle={{ padding: 24 }}
            >
              <Space orientation="vertical" style={{ width: '100%' }} size={16}>
                <Progress
                  percent={Number(summary.usageRate.toFixed(1))}
                  strokeColor={summary.usageRate > 100 ? '#f46b6b' : summary.usageRate >= 85 ? '#f6c453' : '#65d6b8'}
                  railColor="rgba(255,255,255,0.08)"
                />
                <Text style={{ color: DASHBOARD_THEME.textSecondary }}>总预算使用率：{summary.usageRate.toFixed(1)}%</Text>
                <Text style={{ color: DASHBOARD_THEME.textMuted }}>风险由预算、工期、长期无更新和进度异常等规则共同判定</Text>

                <Row gutter={[8, 8]}>
                  <Col><Tag color="green">健康 {summary.levelCounts.healthy}</Tag></Col>
                  <Col><Tag color="gold">预警 {summary.levelCounts.warning}</Tag></Col>
                  <Col><Tag color="orange">危险 {summary.levelCounts.danger}</Tag></Col>
                  <Col><Tag color="red">超支 {summary.levelCounts.over}</Tag></Col>
                  <Col><Tag color="purple">无预算 {summary.noBudgetCount}</Tag></Col>
                  <Col><Tag color="volcano">超期 {summary.overdueCount}</Tag></Col>
                </Row>
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              variant="borderless"
              title={<span style={{ color: DASHBOARD_THEME.textPrimary }}>焦点项目</span>}
              style={{
                borderRadius: 28,
                background: DASHBOARD_THEME.cardBackgroundStrong,
                border: DASHBOARD_THEME.softBorder,
                boxShadow: DASHBOARD_THEME.shadow,
                height: '100%'
              }}
              headStyle={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
              bodyStyle={{ padding: 24, height: 'calc(100% - 57px)' }}
            >
              {focusProject ? (
                <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                  <div>
                    <Text style={{ color: DASHBOARD_THEME.textMuted, fontSize: 12, fontWeight: 700 }}>PRIORITY PROJECT</Text>
                    <Title level={4} style={{ color: DASHBOARD_THEME.textPrimary, margin: '8px 0 0' }}>{focusProject.name}</Title>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                    <div>
                      <Text style={{ color: DASHBOARD_THEME.textMuted, fontSize: 12 }}>预算</Text>
                      <div style={{ color: DASHBOARD_THEME.textPrimary, fontWeight: 700, marginTop: 6 }}>{formatMoney(focusProject.budget || 0)}</div>
                    </div>
                    <div>
                      <Text style={{ color: DASHBOARD_THEME.textMuted, fontSize: 12 }}>成本</Text>
                      <div style={{ color: DASHBOARD_THEME.textPrimary, fontWeight: 700, marginTop: 6 }}>{formatMoney(focusProject.actualCost || 0)}</div>
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text style={{ color: DASHBOARD_THEME.textSecondary }}>预算使用率</Text>
                      <Text style={{ color: DASHBOARD_THEME.textPrimary }}>{focusProject.usage?.toFixed(1) || '0.0'}%</Text>
                    </div>
                    <Progress percent={Math.min(Number(focusProject.usage?.toFixed(1) || 0), 100)} strokeColor={DASHBOARD_THEME.accent} railColor="rgba(16,27,35,0.08)" showInfo={false} />
                  </div>
                  <Button type="primary" style={{ borderRadius: 999, background: DASHBOARD_THEME.accent, borderColor: DASHBOARD_THEME.accent, color: '#161616' }} onClick={() => {
                    setSelectedProject(focusProject);
                    setDrillOpen(true);
                  }}>
                    总成本下钻
                  </Button>
                </Space>
              ) : (
                <Empty description="暂无焦点项目" />
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card
              variant="borderless"
              title={<span style={{ color: DASHBOARD_THEME.textPrimary }}>高风险项目</span>}
              extra={
                <Select
                  value={riskFilter}
                  style={{ width: 180 }}
                  onChange={setRiskFilter}
                  options={[
                    { label: '全部风险', value: 'all' },
                    { label: '超预算', value: '超预算' },
                    { label: '无预算', value: '无预算' },
                    { label: '超期', value: '超期' },
                    { label: '长期无更新', value: '长期无更新' },
                    { label: '进度异常', value: '进度异常' },
                    { label: '预算紧张', value: '预算紧张' },
                  ]}
                />
              }
              style={{
                borderRadius: 28,
                background: DASHBOARD_THEME.cardBackground,
                border: DASHBOARD_THEME.softBorder,
                boxShadow: DASHBOARD_THEME.shadow
              }}
              headStyle={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
              bodyStyle={{ padding: 20 }}
            >
              {riskProjects.length > 0 ? (
                <Table rowKey="_id" columns={columns} dataSource={riskProjects} pagination={false} scroll={{ x: 1000 }} />
              ) : (
                <Empty description="暂无项目数据" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              variant="borderless"
              title={<span style={{ color: DASHBOARD_THEME.textPrimary }}>快捷动作</span>}
              style={{
                borderRadius: 28,
                background: DASHBOARD_THEME.cardBackground,
                border: DASHBOARD_THEME.softBorder,
                boxShadow: DASHBOARD_THEME.shadow,
                height: '100%'
              }}
              headStyle={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
              bodyStyle={{ padding: 24 }}
            >
              <Space orientation="vertical" style={{ width: '100%' }}>
                <Button block style={{ borderRadius: 999 }} onClick={() => navigate('/project')}>项目主数据</Button>
                <Button block style={{ borderRadius: 999 }} onClick={() => navigate('/reports/project-comprehensive')}>经营报表</Button>
                <Button block style={{ borderRadius: 999 }} onClick={() => navigate('/worktime')}>补录工时</Button>
                <Button block style={{ borderRadius: 999 }} onClick={() => navigate('/material')}>补录材料</Button>
                <Button block style={{ borderRadius: 999 }} onClick={() => navigate('/equipment')}>补录设备</Button>
                <Button block type="primary" style={{ borderRadius: 999, background: DASHBOARD_THEME.accent, borderColor: DASHBOARD_THEME.accent, color: '#161616' }} onClick={() => openQuickAdd('subcontract')}>新增分包成本</Button>
                <Button block type="primary" style={{ borderRadius: 999, background: DASHBOARD_THEME.accentSecondary, borderColor: DASHBOARD_THEME.accentSecondary }} onClick={() => openQuickAdd('coordination')}>新增协调成本</Button>
              </Space>
            </Card>
          </Col>
        </Row>

      </Space>

      <Drawer
        title={`经营下钻 - ${selectedProject?.name || ''}`}
        width={560}
        open={drillOpen}
        onClose={() => setDrillOpen(false)}
      >
        {drilldownQuery.isLoading ? (
          <div style={{ textAlign: 'center', paddingTop: 80 }}>
            <Spin />
          </div>
        ) : drilldownQuery.data ? (
          <Space orientation="vertical" style={{ width: '100%' }} size={16}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="项目预算">{formatMoney(drilldownQuery.data.project?.budget || selectedProject?.budget || 0)}</Descriptions.Item>
              <Descriptions.Item label="累计统计成本">{formatMoney(totalProjectCost)}</Descriptions.Item>
              <Descriptions.Item label="预算使用率">
                {typeof drilldownQuery.data.summary?.budgetUsageRate === 'number' ? `${drilldownQuery.data.summary.budgetUsageRate.toFixed(1)}%` : '未设置预算'}
              </Descriptions.Item>
              <Descriptions.Item label="工期状态">{drilldownQuery.data.summary?.scheduleStatus?.label || '-'}</Descriptions.Item>
              <Descriptions.Item label="手动进度">{drilldownQuery.data.project?.manualProgress ?? selectedProject?.progress ?? 0}%</Descriptions.Item>
              <Descriptions.Item label="风险标签">
                <Space wrap>
                  {(drilldownQuery.data.summary?.risk?.riskTags || []).map((tag) => <Tag key={tag}>{tag}</Tag>)}
                  {!(drilldownQuery.data.summary?.risk?.riskTags || []).length && <Tag>正常</Tag>}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '8px 0' }}>成本构成</Divider>

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="人工成本">{formatMoney(breakdown?.workTimeCost || 0)}</Descriptions.Item>
              <Descriptions.Item label="设备成本">{formatMoney(breakdown?.equipmentCost || 0)}</Descriptions.Item>
              <Descriptions.Item label="材料成本">{formatMoney(breakdown?.materialCost || 0)}</Descriptions.Item>
              <Descriptions.Item label="分包成本">{formatMoney(breakdown?.subcontractCost || 0)}</Descriptions.Item>
              <Descriptions.Item label="协调成本">{formatMoney(breakdown?.coordinationCost || 0)}</Descriptions.Item>
              <Descriptions.Item label="分包口径说明">{drilldownQuery.data.summary?.subcontractRule?.description || '优先已付金额，缺失时回退合同金额'}</Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '8px 0' }}>异常提示</Divider>
            <Space orientation="vertical" style={{ width: '100%' }} size={8}>
              {(drilldownQuery.data.summary?.risk?.reasons || []).length > 0 ? (
                drilldownQuery.data.summary.risk.reasons.map((reason, index) => (
                  <Alert key={index} type="warning" showIcon message={reason} />
                ))
              ) : (
                <Alert type="success" showIcon message="当前项目暂无明显经营异常" />
              )}
            </Space>

            <Divider style={{ margin: '8px 0' }}>处理动作</Divider>
            <Space wrap>
              <Button onClick={() => navigate(`/dashboard?projectId=${selectedProject?.id || selectedProject?._id}`)}>查看经营详情</Button>
              <Button onClick={() => navigate(`/reports/project-comprehensive?projectId=${selectedProject?._id || selectedProject?.id}`)}>查看经营报表</Button>
              <Button onClick={() => navigate(`/worktime?projectId=${selectedProject?._id || selectedProject?.id}`)}>查看工时记录</Button>
              <Button onClick={() => navigate(`/material?projectId=${selectedProject?._id || selectedProject?.id}`)}>查看材料记录</Button>
              <Button onClick={() => navigate(`/equipment?projectId=${selectedProject?._id || selectedProject?.id}`)}>查看设备记录</Button>
              <Button type="primary" onClick={() => navigate(`/worktime?projectId=${selectedProject?._id || selectedProject?.id}`)}>补录工时</Button>
              <Button type="primary" onClick={() => navigate(`/material?projectId=${selectedProject?._id || selectedProject?.id}`)}>补录材料</Button>
              <Button type="primary" onClick={() => navigate(`/equipment?projectId=${selectedProject?._id || selectedProject?.id}`)}>补录设备</Button>
            </Space>
          </Space>
        ) : (
          <Empty description="暂无下钻数据" />
        )}
      </Drawer>

      <Modal
        title={quickAddType === 'subcontract' ? '新增分包成本' : '新增协调成本'}
        open={quickAddVisible}
        onCancel={closeQuickAdd}
        onOk={handleQuickAddSubmit}
        okText="保存"
        confirmLoading={createSubcontractMutation.isPending || createCoordinationMutation.isPending}
        destroyOnHidden
      >
        <Form form={quickAddForm} layout="vertical">
          <Form.Item name="projectId" label="项目" rules={[{ required: true, message: '请选择项目' }]}>
            <Select
              placeholder="请选择项目"
              showSearch
              optionFilterProp="label"
              options={projects.map((p: any) => ({ label: p.name, value: p._id }))}
            />
          </Form.Item>

          {quickAddType === 'subcontract' ? (
            <>
              <Form.Item name="contractName" label="合同名称" rules={[{ required: true, message: '请输入合同名称' }]}>
                <Input placeholder="请输入合同名称" />
              </Form.Item>
              <Form.Item name="costDate" label="日期" rules={[{ required: true, message: '请选择日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="contractAmount" label="合同金额" rules={[{ required: true, message: '请输入合同金额' }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入合同金额" />
              </Form.Item>
              <Form.Item name="paidAmount" label="已付金额">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入已付金额" />
              </Form.Item>
              <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select options={[{ label: '进行中', value: 'active' }, { label: '已完成', value: 'completed' }, { label: '已取消', value: 'cancelled' }]} />
              </Form.Item>
              <Form.Item name="description" label="备注">
                <TextArea rows={3} placeholder="备注（可选）" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item name="costDate" label="日期" rules={[{ required: true, message: '请选择日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="costType" label="费用类型" rules={[{ required: true, message: '请选择费用类型' }]}>
                <Select options={CoordinationCostService.getCostTypeOptions()} />
              </Form.Item>
              <Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入金额" />
              </Form.Item>
              <Form.Item name="handler" label="经办人">
                <Input placeholder="请输入经办人" />
              </Form.Item>
              <Form.Item name="description" label="备注">
                <TextArea rows={3} placeholder="备注（可选）" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectControlCenter;
